import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import RedesignSidebar from './RedesignSidebar';
import RedesignHeader from './RedesignHeader';
import MobileTabBar from './MobileTabBar';
import MoreSheet from './MoreSheet';
import { adminGroupsFor } from './adminNav';
import BusinessMenu from './BusinessMenu';
import { isMarketplaceEnabled, isOpportunityFeedEnabled } from '../../config/features';
import { tabsFor } from './navigation';
import ImpersonationBanner from '../../components/Admin/ImpersonationBanner';
import FreeTrialBanner from '../FreeTrialBanner';
import EmailVerificationBanner from '../EmailVerificationBanner';
import { enquiriesApi } from '../../services/enquiriesApi';
import { useAuth } from '../../contexts/useAuth';
import { hasDaIntelligence } from '../../utils/entitlements';
import { refreshNewSavedCount, useNewSavedCount } from '../../hooks/useNewSavedCount';

/** sessionStorage key for the phone's folded / unfolded account notices. */
const BANNERS_COLLAPSED_KEY = 'account-banners-collapsed';

/**
 * The prototype's shell.
 *
 * Stamping data-app-redesign on <html> is what activates the scoped brand-* override in index.css.
 * Without it the retained legacy pages keep the old ramp and look orphaned inside the new chrome.
 * It is removed on unmount so nothing leaks between tests, or between a gate flip and a re-render.
 */
export default function RedesignLayout() {
    const [moreOpen, setMoreOpen] = useState(false);
    // The mobile half of the business switcher. The topbar chip that opens the desktop menu is
    // `hidden md:flex`, so the More sheet cannot simply proxy a click to it — it opens this second
    // BusinessMenu instead, placed for a phone rather than hung under a chip that isn't there.
    const [businessMenuOpen, setBusinessMenuOpen] = useState(false);
    // No trigger to wrap this time (the sheet closes before the menu opens), so the boundary is
    // the menu alone — still passed explicitly because BusinessMenu has no other way to know what
    // counts as "outside". See its boundaryRef doc comment.
    const businessMenuRef = useRef<HTMLDivElement>(null);
    const [enquiriesCount, setEnquiriesCount] = useState(0);
    const { logout, user, activeBusiness } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const newSavedCount = useNewSavedCount();
    // Hidden on Saved itself: the page is showing the new rows (with their pills) right now, and
    // the count would otherwise linger until its saved/viewed call came back.
    const savedBadgeCount = pathname === '/saved' ? 0 : newSavedCount;

    useEffect(() => {
        document.documentElement.setAttribute('data-app-redesign', '');
        return () => document.documentElement.removeAttribute('data-app-redesign');
    }, []);

    const flags = { marketplace: isMarketplaceEnabled(), deals: isOpportunityFeedEnabled() };

    useEffect(() => {
        // Only when the marketplace is live: with the flag off there is no Enquiries entry in
        // either the sidebar or the tab bar to badge, and no listing to have generated an enquiry
        // in the first place, so there is nothing this count could ever be used for.
        if (!flags.marketplace) return;

        let cancelled = false;
        // A failed fetch just leaves the badge absent rather than surfacing an error — this is a
        // passive count feeding a decorative badge, not a user-initiated action, so there is
        // nothing here for a toast to be actionable about (contrast the status-change actions in
        // Enquiries.tsx, which really do need to surface a failure).
        enquiriesApi
            .countNew()
            .then(({ count }) => { if (!cancelled) setEnquiriesCount(count); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [flags.marketplace]);

    const onIntelligence = hasDaIntelligence(activeBusiness);
    useEffect(() => {
        // Saved is an Intelligence screen; without the plan the server refuses the count, and the
        // sidebar shows Saved as a PRO upsell with no badge to fill. Re-asked when the business
        // changes, because the saves (and so the count) belong to the business.
        if (!onIntelligence) return;
        void refreshNewSavedCount();
    }, [onIntelligence, activeBusiness?.id]);

    // The mobile account-banner strip, and the bottom tab bar it is pinned above. Both heights are
    // MEASURED rather than stated: all three banners are conditional, they stack, and their text
    // wraps at phone widths, so the strip has no height that could be written down; the tab bar's
    // is the sum of its own padding, icon, label line-box, border and safe-area inset, which is
    // MobileTabBar's business and not this component's to restate. Deriving either by re-checking
    // the banners' own render conditions here would put those conditions in two places and get
    // them wrong the first time one of them changed.
    const bannerStripRef = useRef<HTMLDivElement>(null);
    const [bannerStripHeight, setBannerStripHeight] = useState(0);
    /**
     * Whether the phone's account notices are folded down to a one-line bar. Per session, not
     * forever: a trial or impersonation notice folded away today should be back in view tomorrow.
     * Guarded, because storage can throw (private mode, blocked site data).
     */
    const [bannersCollapsed, setBannersCollapsed] = useState(() => {
        try { return sessionStorage.getItem(BANNERS_COLLAPSED_KEY) === '1'; } catch { return false; }
    });
    const toggleBanners = () => setBannersCollapsed((collapsed) => {
        const next = !collapsed;
        try { sessionStorage.setItem(BANNERS_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* per-view only */ }
        return next;
    });
    const [tabBarHeight, setTabBarHeight] = useState(0);

    useEffect(() => {
        const strip = bannerStripRef.current;
        // Queried, not reffed, because the tab bar is `position: fixed` — a wrapper element around
        // it would have zero height and measure nothing.
        const tabBar = document.querySelector<HTMLElement>('[data-mobile-tab-bar]');

        const measure = () => {
            setBannerStripHeight(strip?.getBoundingClientRect().height ?? 0);
            setTabBarHeight(tabBar?.getBoundingClientRect().height ?? 0);
        };
        measure();

        // Both elements go to `display: none` from `md:` up (the tab bar) or when every banner
        // returns null (the strip, via `empty:hidden`), which reports as height 0 — so crossing the
        // breakpoint or dismissing the last banner releases the reserved space without this
        // component knowing why either one became empty.
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        if (strip) observer.observe(strip);
        if (tabBar) observer.observe(tabBar);
        return () => observer.disconnect();
    }, []);

    // Computed once, here, and passed to MobileTabBar as a prop rather than left for it to
    // re-derive from the flags itself — the promotion rule lives in exactly one place. The More
    // sheet's contents are derived from this same array: whatever key tabsFor did not use for a
    // slot is a key the sheet still needs to offer. Marketplace itself is never a tab-bar slot, so
    // it is gated on the flag directly, and Plan has no tab-bar equivalent to be promoted out of,
    // so it is always present.
    const tabs = tabsFor(flags);
    const barKeys = new Set(tabs.map((tab) => tab.key));
    const moreSheetItems = [
        { key: 'switch-business', label: 'Switch business', onSelect: () => setBusinessMenuOpen(true) },
        ...(barKeys.has('saved') ? [] : [{ key: 'saved', label: 'Saved', to: '/saved', badge: savedBadgeCount }]),
        ...(barKeys.has('alerts') ? [] : [{ key: 'alerts', label: 'Alert settings', to: '/alert-settings' }]),
        ...(flags.marketplace ? [
            { key: 'listing', label: 'Marketplace', to: '/my-listing' },
            { key: 'send-my-business', label: 'Send my Business', to: '/send-my-business' },
        ] : []),
        // The admin tools, for admins only — the same list, via the same function, as the desktop
        // sidebar's Management / Admin sections. Without these a phone had no way into any admin
        // screen. Empty for everyone else, so a client's sheet is unchanged.
        ...adminGroupsFor(user).flatMap((group) => group.links.map((link) => ({
            key: `admin-${link.to}`,
            label: link.label,
            to: link.to,
            group: group.heading,
        }))),
        // /plan, not /billing: the same destination the sidebar's plan card now goes to, so the two
        // Plan affordances cannot disagree about what "Plan" means. /plan's own buttons hand off to
        // /billing for anything that actually changes a subscription.
        { key: 'plan', label: 'Plan', to: '/plan' },
        // The prototype's own More sheet omits these — its MORE array carries the comment
        // "Account & sign out removed: the hero avatar now opens the account menu directly" — but
        // that hero doesn't exist in this app yet (the dashboards are unbuilt), and the sidebar
        // that carries the account menu is `hidden md:flex`. Without these two entries a phone
        // would have no way to reach account settings or sign out at all. Revisit (most likely:
        // remove) once a dashboard hero avatar exists to open the account menu on mobile too.
        { key: 'account', label: 'Account settings', to: '/profile' },
        // Paired with Account settings for the same reason as in the desktop UserMenu: the two
        // settings surfaces, the user's profile and the business record behind it.
        { key: 'business-details', label: 'Business details', to: '/business-details' },
        {
            key: 'logout',
            label: 'Log out',
            // Matches the desktop UserMenu's red treatment of the same action, so the two
            // sign-out affordances don't look different across breakpoints.
            destructive: true,
            onSelect: async () => {
                // Same flow as legacy UserDropdown and the desktop UserMenu: await the server
                // logout before navigating away.
                await logout();
                navigate('/login');
            },
        },
    ];

    return (
        <div className="flex bg-canvas min-h-dvh">
            <RedesignSidebar enquiriesCount={enquiriesCount} savedCount={savedBadgeCount} />
            <div className="flex-1 min-w-0 text-ink">
                {/* The app's pinned chrome, as ONE sticky block rather than four sticky siblings.
                    Siblings would each need to know the heights of those above them, and all three
                    banners are conditional, so those offsets would be wrong for most combinations.

                    `bg-canvas` is load-bearing: every banner's dark-mode fill is
                    `bg-amber-500/10` — translucent — so pinned over scrolling content they would
                    show the rows sliding underneath. The opaque wrapper is what they sit on.

                    z-30, NOT the legacy AppHeader's z-99999. That number is why ContactComposer
                    and NewDealDialog both sit at z-99999 themselves — each had to
                    outbid a sticky header. This only has to clear page content; the popovers and
                    menus the bar opens are its own children and stack above it regardless.

                    `data-sticky-chrome` is how `scrollRegionIntoView` measures what is covering the
                    top of the viewport, instead of assuming a height. */}
                <div data-sticky-chrome className="sticky top-0 z-30 bg-canvas">
                    <RedesignHeader />
                    {/* BELOW the bar, not above it: the bar is the same on every page, these are
                        notices about one account's state, and above the bar the temporary kind was
                        pushing the permanent kind down.

                        Admin routes render inside this shell, so an impersonating admin needs the
                        same indicator and "Return to admin" control the legacy shell gives them.
                        All three return null when inapplicable, so no conditionals are needed
                        here; see `empty:hidden` on the wrapper below for what happens when all
                        three do. */}
                    {/* On a phone these leave the top chrome entirely and pin themselves
                        immediately above the tab bar — the placement the user chose after asking
                        for "another place for the notification bar where it shows X days left on
                        your trial". They stay in the sticky chrome from `md:` up (`md:static`,
                        which also makes the `bottom`/`z-index` below inert there, since neither
                        applies to a static box).

                        Still rendered HERE, inside the chrome, rather than next to MobileTabBar:
                        one mount, in one place, positioned differently per breakpoint. A second
                        copy under the tab bar would mean two ImpersonationBanners with their own
                        request state and two EmailVerificationBanners each with their own
                        dismissed flag.

                        `empty:hidden` is what keeps this from being a stray opaque bar across the
                        bottom of every page: with all three banners returning null the wrapper has
                        no element children at all, so `:empty` matches — and it matches without
                        this component knowing, or restating, why any of them returned null.

                        `z-80` reads below the tab bar's `z-90` and the More sheet's `z-95`/`z-96`,
                        which is the intent; in practice the chrome's own `z-30` is a stacking
                        context, so the strip paints at 30 — still above page content and still
                        under the sheet. It never overlaps the tab bar because it sits on top of
                        it. */}
                    <div
                        ref={bannerStripRef}
                        data-testid="account-banners"
                        style={{ bottom: tabBarHeight }}
                        className="fixed left-0 right-0 z-80 flex flex-col bg-canvas md:static md:z-auto"
                    >
                        {/* The banners themselves. `peer` + `empty:hidden`: with all three returning
                            null this box is empty and hidden, and the toggle after it — `peer-empty`
                            — hides with it, so there is never a toggle for nothing. The toggle is
                            AFTER it in the DOM because a peer must precede its sibling; `order-first`
                            draws it on top. */}
                        <div
                            id="account-banners-body"
                            data-testid="account-banners-body"
                            className={`peer empty:hidden ${bannersCollapsed ? 'max-md:hidden' : ''}`}
                        >
                            <ImpersonationBanner />
                            <FreeTrialBanner />
                            <EmailVerificationBanner />
                        </div>
                        {/* Phones only: from md: up the notices sit in the top chrome, where they
                            are out of the way already. */}
                        <button
                            type="button"
                            aria-expanded={!bannersCollapsed}
                            aria-controls="account-banners-body"
                            onClick={toggleBanners}
                            className="order-first flex w-full items-center justify-between border-t border-border bg-surface px-4 py-1.5 text-[12px] font-semibold text-ink-muted peer-empty:hidden md:hidden"
                        >
                            <span>{bannersCollapsed ? 'Account notices' : 'Hide notices'}</span>
                            <span aria-hidden="true" className="text-[10px]">{bannersCollapsed ? '▲' : '▼'}</span>
                        </button>
                    </div>
                </div>
                {/*
                    5rem (80px) is what stops the fixed mobile tab bar covering the last row of
                    content — the prototype sets exactly that, and it clears the bar with room to
                    spare. The banner strip is pinned on TOP of the bar, so its measured height has
                    to be added on or it covers the last rows instead.

                    Routed through a custom property rather than an inline `paddingBottom` on
                    purpose: an inline style would beat `md:py-[30px]` and leak the phone's reserved
                    space onto the desktop page, where there is no strip and no tab bar. As a
                    variable it stays inside the class layer, where the `md:` restore still wins.
                */}
                <div
                    data-testid="redesign-content"
                    style={{ '--mobile-banner-strip-height': `${bannerStripHeight}px` } as CSSProperties}
                    className="p-4 pb-[calc(5rem+var(--mobile-banner-strip-height,0px))] md:px-9 md:py-[30px]"
                >
                    <Outlet />
                </div>
            </div>
            <MobileTabBar
                tabs={tabs}
                moreOpen={moreOpen}
                onToggleMore={() => setMoreOpen((open) => !open)}
                enquiriesCount={enquiriesCount}
                savedCount={savedBadgeCount}
                // Saved in the More sheet (not a tab): its new-saves count goes on More, as the
                // sidebar badges it on desktop — otherwise a phone never shows it at all.
                moreCount={barKeys.has('saved') ? 0 : savedBadgeCount}
            />
            <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} items={moreSheetItems} />
            {/* Rendered after MoreSheet so that when the sheet closes and this opens in the same
                commit, the sheet's focus-restore cleanup lands before this menu takes focus. */}
            <div ref={businessMenuRef}>
                <BusinessMenu
                    open={businessMenuOpen}
                    onClose={() => setBusinessMenuOpen(false)}
                    boundaryRef={businessMenuRef}
                    // Sits above the tab bar rather than under a chip — there is no chip at this
                    // breakpoint. `md:hidden` because the header's own instance owns the desktop
                    // case, and only one of the two should ever be reachable.
                    positionClassName="md:hidden fixed left-4 right-4 bottom-24"
                />
            </div>
        </div>
    );
}
